import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useWorkspaces, useTemplates } from '../hooks/useClaw9';

/**
 * Modal for creating a new workspace
 */
function CreateWorkspaceModal({ onClose, onCreated }) {
    const { createWorkspace, isLoading: isCreating } = useWorkspaces({ autoFetch: false });
    const { templates, isLoading: loadingTemplates } = useTemplates();

    const [botId, setBotId] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const trimmedBotId = botId.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '_');

        if (!trimmedBotId) {
            setError('Bot ID is required.');
            return;
        }

        if (trimmedBotId.length < 3) {
            setError('Bot ID must be at least 3 characters.');
            return;
        }

        try {
            await createWorkspace({
                botId: trimmedBotId,
                templateId: selectedTemplate || undefined
            });
            onCreated?.();
        } catch (err) {
            setError(err.message || 'Failed to create workspace.');
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                {/* Backdrop */}
                <div
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    onClick={onClose}
                />

                {/* Modal */}
                <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle bg-white rounded-lg shadow-xl transform transition-all">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium text-gray-900">
                            Create Workspace
                        </h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        {/* Bot ID */}
                        <div className="mb-4">
                            <label htmlFor="workspace-bot-id" className="block text-sm font-medium text-gray-700 mb-1">
                                Bot ID
                            </label>
                            <input
                                id="workspace-bot-id"
                                type="text"
                                value={botId}
                                onChange={(e) => setBotId(e.target.value)}
                                placeholder="e.g., my-sales-bot"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                disabled={isCreating}
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                Unique identifier for your workspace. Use lowercase letters, numbers, dashes, and underscores.
                            </p>
                        </div>

                        {/* Template Selection */}
                        <div className="mb-4">
                            <label htmlFor="workspace-template" className="block text-sm font-medium text-gray-700 mb-1">
                                Template (Optional)
                            </label>
                            <select
                                id="workspace-template"
                                value={selectedTemplate}
                                onChange={(e) => setSelectedTemplate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                disabled={isCreating || loadingTemplates}
                            >
                                <option value="">Start from scratch</option>
                                {templates.map(template => (
                                    <option key={template.template_id} value={template.template_id}>
                                        {template.name}
                                    </option>
                                ))}
                            </select>
                            <p className="mt-1 text-xs text-gray-500">
                                Choose a template to pre-populate your workspace with knowledge files.
                            </p>
                        </div>

                        {/* Knowledge Files Info */}
                        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                            <h4 className="text-xs font-medium text-gray-700 mb-1">Workspace will include:</h4>
                            <ul className="text-xs text-gray-600 space-y-1">
                                <li>• SOUL.md - Bot personality and purpose</li>
                                <li>• IDENTITY.md - Bot identity details</li>
                                <li>• AGENTS.md - Agent definitions</li>
                                <li>• SALES_KNOWLEDGE.md - Sales scripts</li>
                                <li>• knowledge_base.csv - Structured data</li>
                            </ul>
                        </div>

                        {/* Error */}
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                disabled={isCreating}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                                disabled={isCreating}
                            >
                                {isCreating ? 'Creating...' : 'Create Workspace'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

CreateWorkspaceModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onCreated: PropTypes.func
};

export default CreateWorkspaceModal;
